import { useCallback, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { useDropzone } from 'react-dropzone';
import useBoundStore from 'store';
import { uploadFacilitiesPhoto } from 'api';
import { BuyApartmentFlowPaths, SellApartmentFlowPaths } from 'routes';
import useApartmentSaleInfo, { APIApartmentSellInfoRes } from 'api/hooks/use-apartment-sale-info';
import BaseScreen from 'components/base-screen';
import BottomSheet from 'components/bottom-sheet';
import Button, { ButtonProps } from 'components/button';
import BottomSheetResult from 'components/bottom-sheet-result';
import ApartmentListItem from 'components/apartment-list-item';
import iconUpload from '../../assets/images/icon-file-upload.png';
import iconDelete from '../../assets/images/icon-delete-cross.svg';
import iconFile from '../../assets/images/icon-files.svg';
import styles from './styles.module.scss';

export type InputApartmentPriceNavigateProps = {
  apartment: APIApartmentSellInfoRes & { id: number; price: number };
};

const SellUploadPhotoScreen: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { state } = location;

  const selectedSellBuilding = useBoundStore((s) => s.selectedSellBuilding);
  const sellApartmentNumber = useBoundStore((s) => s.sellApartmentNumber);
  const navigationProps = useLocation().state as InputApartmentPriceNavigateProps | undefined;

  const saleInfo = useApartmentSaleInfo(
    selectedSellBuilding.id,
    navigationProps ? '' : sellApartmentNumber,
  );

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false);
  const [files, setFiles] = useState([]);

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      // @ts-ignore
      setFiles([...files, ...acceptedFiles]);
    },
    [files, setFiles],
  );

  const { getRootProps, getInputProps } = useDropzone({
    multiple: false,
    onDrop,
  });

  const handleRemoveFileInput = (index: any) => {
    const newFiles = [...files];
    newFiles.splice(index, 1);
    setFiles(newFiles);
  };

  const buttons: ButtonProps[] = [
    {
      label: 'Отправить',
      disabled: !files.length,
      loading: isSubmitting,
      variant: 'secondary',
      onClick: async () => {
        const formData = new FormData();
        files.forEach((file) => {
          formData.append(`files`, file);
        });
        setIsSubmitting(true);
        const uploadDocumentsRes = await uploadFacilitiesPhoto(
          // @ts-ignore
          state ? state?.id : null,
          formData,
        );
        setIsSubmitting(false);
        if (uploadDocumentsRes.result) {
          navigate(SellApartmentFlowPaths.mainSellChooseMode);
          useBoundStore.getState().showToast({
            message: 'Фотографии добавлены успешно',
          });
        } else {
          useBoundStore.getState().showToast({
            message: 'Ошибка добавления фотографий',
          });
        }
      },
    },
  ];

  const handleDismissBottomSheet = () => {
    setIsBottomSheetOpen(false);
    navigate(BuyApartmentFlowPaths.main);
  };

  return (
    <BaseScreen
      padContent
      title='Загрузите фотографии'
      buttons={buttons}
      onBackButtonClick={() => navigate(-1)}
    >
      <h1 className={styles.title}>Фотографии</h1>
      <div {...getRootProps()} className={clsx(styles.labelFileUpload)}>
        <input {...getInputProps()} />
        <div className={styles.uploadContent}>
          <img src={iconUpload} alt='Добавьте фотографии' />
          <div className={styles.uploadButton}>Добавить фотографии</div>
          <p>{'PNG, TIFF, JPG'}</p>
        </div>
      </div>

      <div className={styles.fileList}>
        {files.map((file: any, index) => {
          return (
            <div className={styles.uploadDocument} key={index}>
              <div>
                <img src={iconFile} />
              </div>
              <p className={styles.titleDocument}>{file?.name}</p>
              <button type='button' onClick={() => handleRemoveFileInput(index)}>
                <img src={iconDelete} alt='Удалить' />
              </button>
            </div>
          );
        })}
      </div>

      <BottomSheet open={isBottomSheetOpen} onDismiss={handleDismissBottomSheet}>
        <BottomSheetResult type='info' header='Заявка принята'>
          Ваша заявка отправлена на модерацию
        </BottomSheetResult>
        <Button label='Отлично' onClick={handleDismissBottomSheet} variant={'secondary'} />
      </BottomSheet>
    </BaseScreen>
  );
};

export default SellUploadPhotoScreen;
